import { ActionPanelScrollProvider } from "@/components/mentor-workflows/action/action-panel-scroll";
import {
  ACTION_DEFAULT,
  ACTION_RAIL,
} from "@/components/mentor-workflows/column-layout";
import {
  ActionHeader,
  CollapsedRail,
} from "@/components/mentor-workflows/action/action-workspace";
import {
  ACTION_TABS,
  DEFAULT_ACTION_TAB,
  type ActionTab,
} from "@/components/mentor-workflows/action/action-tab";
import { ToolRow } from "@/components/mentor-workflows/action/tools/tool-row";
import { CONTENT_TOOLS } from "@/components/mentor-workflows/action/tools/tool-config";
import { ContentToolThumbnail } from "@/components/mentor-workflows/action/tools/tool-thumbnail";
import type { TrainingLevelTeen } from "@/lib/api/training-simulator/training-simulator";
import { Segmented } from "@somethings-ui/web";
import { IconButton } from "@somethings-ui/web";
import { Bell, ChevronsLeft, FileText, Pencil, Plus } from "lucide-react";
import { useState } from "react";

interface TrainingSessionProfilePanelProps {
  teen: TrainingLevelTeen | undefined;
}

const SUMMARY_PLACEHOLDER =
  "An AI-generated summary of your mentee will appear here after a few conversations. It builds over time.";

export function TrainingSessionProfilePanel({
  teen,
}: TrainingSessionProfilePanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<ActionTab>(DEFAULT_ACTION_TAB);

  if (!teen) return null;

  return (
    <aside
      aria-label="Action"
      className="hidden h-full shrink-0 flex-col overflow-hidden border-l border-surfaces-z4 bg-white lg:flex"
      style={{ width: collapsed ? ACTION_RAIL : ACTION_DEFAULT }}
    >
      {collapsed ? (
        <CollapsedRail
          tabs={ACTION_TABS}
          activeTab={activeTab}
          onExpand={(next) => {
            setActiveTab(next);
            setCollapsed(false);
          }}
        >
          <IconButton
            variant="tertiary"
            size="md"
            shape="circle"
            aria-label="Expand action panel"
            icon={<ChevronsLeft className="size-5" />}
            onClick={() => setCollapsed(false)}
          />
        </CollapsedRail>
      ) : (
        <div className="relative flex h-full min-h-0 flex-col">
          <ActionHeader
            tabs={ACTION_TABS}
            activeTab={activeTab}
            onSelectTab={setActiveTab}
            onCollapse={() => setCollapsed(true)}
          />
          <div className="flex min-h-0 flex-1 flex-col">
            <ActionPanelScrollProvider>
              <TrainingActionBody tab={activeTab} teen={teen} />
            </ActionPanelScrollProvider>
          </div>
        </div>
      )}
    </aside>
  );
}

function TrainingActionBody({
  tab,
  teen,
}: {
  tab: ActionTab;
  teen: TrainingLevelTeen;
}) {
  switch (tab) {
    case "profile":
      return <TrainingProfile teen={teen} />;
    case "context":
      return <TrainingContextPlaceholder />;
    case "tools":
      return <TrainingToolsPlaceholder />;
    case "history":
      return <TrainingHistoryPlaceholder />;
    default: {
      const unreachable: never = tab;
      return unreachable;
    }
  }
}

function TrainingProfile({ teen }: { teen: TrainingLevelTeen }) {
  const subtitle = [
    teen.age ? `${teen.age} y/o` : null,
    teen.pronouns,
    teen.gender ? formatGender(teen.gender) : null,
  ].filter((part): part is string => !!part && part.length > 0);
  const interests = teen.profile.topics_selected ?? [];
  const documentation = teen.profile.documentation_to_mentor;

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-body text-xl font-bold leading-7 text-text-primary">
          {teen.display_name}
        </h2>
        {subtitle.length > 0 ? (
          <p className="font-body text-body-2 text-text-tertiary">
            {subtitle.join(" · ")}
          </p>
        ) : null}
      </div>

      {interests.length > 0 ? (
        <section className="flex flex-col gap-3 rounded-[20px] border border-surfaces-divider p-4">
          <h3 className="font-mono text-xs font-medium tracking-[-0.24px] text-text-tertiary">
            INTERESTS
          </h3>
          <div className="flex flex-wrap gap-2">
            {interests.map((interest) => (
              <span
                key={interest}
                className="rounded-full bg-surfaces-z3 px-3 py-1 font-body text-body-3 text-text-secondary"
              >
                {interest}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {documentation ? (
        <section className="flex flex-col gap-2 rounded-[20px] bg-surfaces-z3 p-4">
          <h3 className="font-mono text-xs font-medium tracking-[-0.24px] text-text-tertiary">
            DOCUMENTATION
          </h3>
          <p className="whitespace-pre-wrap font-body text-body-2 text-text-secondary">
            {documentation}
          </p>
        </section>
      ) : null}
    </div>
  );
}

function TrainingContextPlaceholder() {
  return (
    <div className="flex flex-col">
      <section className="px-4 pt-4">
        <div className="flex flex-col gap-2 rounded-[20px] border border-dashed border-surfaces-divider bg-white p-4">
          <div className="flex items-center gap-1.5 text-text-placeholder">
            <FileText className="size-3" />
            <span className="font-body text-body-4">Summary</span>
          </div>
          <p className="font-body text-body-2 text-text-placeholder">
            {SUMMARY_PLACEHOLDER}
          </p>
        </div>
      </section>
      <section className="px-4 pb-3 pt-4">
        <div className="flex flex-col gap-1 rounded-[20px] bg-surfaces-z3 p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-mono text-xs font-medium tracking-[-0.24px] text-text-tertiary">
              DOCUMENTATION
            </h3>
            <button
              type="button"
              disabled
              className="flex cursor-not-allowed items-center gap-1 rounded-xl p-1.5 opacity-50"
            >
              <Plus className="size-4 text-text-tertiary" />
              <span className="font-body text-body-3 text-text-tertiary">Add</span>
            </button>
          </div>
          <p className="font-body text-body-1 text-text-primary">
            Your private space to track what matters
          </p>
          <p className="font-body text-body-2 text-text-secondary">
            Jot down things you notice, reminders for next time, or anything you
            want to remember. AI will also surface patterns it picks up from your
            conversations.
          </p>
          <div className="flex flex-col gap-2 py-2">
            <div className="flex items-center gap-2">
              <Pencil className="size-3 shrink-0 text-text-tertiary" />
              <span className="font-body text-body-3 text-text-tertiary">
                Write documentation after check-ins to build context over time
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Bell className="size-3 shrink-0 text-text-tertiary" />
              <span className="font-body text-body-3 text-text-tertiary">
                Set reminders so nothing slips through the cracks
              </span>
            </div>
          </div>
          <button
            type="button"
            disabled
            className="w-fit cursor-not-allowed rounded-full bg-black px-4 py-2 font-body text-body-3 text-white opacity-50"
          >
            Write a note
          </button>
        </div>
      </section>
    </div>
  );
}

function TrainingToolsPlaceholder() {
  return (
    <div className="flex flex-col pb-4">
      <section className="flex flex-col">
        <div className="px-4 pb-2 pt-4">
          <span className="font-body text-[10px] uppercase tracking-wide text-text-tertiary">
            Activity
          </span>
        </div>
        <p className="px-4 py-2 font-body text-body-3 text-text-tertiary">
          No activities right now - check back soon.
        </p>
      </section>
      <section className="flex flex-col">
        <div className="px-4 pb-2 pt-4">
          <span className="font-body text-[10px] uppercase tracking-wide text-text-tertiary">
            Content
          </span>
        </div>
        <div className="flex flex-col px-2">
          {CONTENT_TOOLS.map((tool) => (
            <ToolRow
              key={tool.id}
              thumbnail={<ContentToolThumbnail id={tool.id} />}
              title={tool.label}
              disabled
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function TrainingHistoryPlaceholder() {
  return (
    <div className="flex flex-col">
      <div className="flex items-center px-4 py-3">
        <Segmented
          options={[
            { value: "list", label: "All" },
            { value: "calendar", label: "Calendar" },
          ]}
          value="list"
          onChange={() => undefined}
          aria-label="History view"
        />
      </div>
      <p className="px-4 py-6 font-body text-body-3 text-text-tertiary">
        No history yet - activities and content will show up here.
      </p>
    </div>
  );
}

function formatGender(gender: string) {
  return gender.charAt(0).toUpperCase() + gender.slice(1);
}
